import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const cardVariants = cva(
  "flex flex-col border border-border bg-card text-card-foreground transition-colors duration-150 ease-out",
  {
    variants: {
      radius: {
        none: "rounded-none",
        default: "rounded-md",
        xl: "rounded-md",
        "2xl": "rounded-md",
      },
      elevation: {
        default: "gap-4 py-5 shadow-card",
        soft: "gap-4 py-5 shadow-card",
        flat: "gap-4 py-5 border-transparent bg-transparent shadow-none",
      },
      interactive: {
        // A colour-only hover, never a lift or a bigger shadow — the flat
        // surface stays flat, it just tells you it is clickable.
        true: "hover:border-ring/30 hover:bg-muted/20",
        false: "",
      },
    },
    defaultVariants: {
      radius: "default",
      elevation: "default",
      interactive: false,
    },
  }
)

function Card({
  className,
  radius,
  elevation,
  interactive,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      data-radius={radius}
      data-elevation={elevation}
      data-interactive={interactive}
      className={cn(cardVariants({ radius, elevation, interactive }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-5 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-4",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("text-section leading-none", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-5", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-5 [.border-t]:pt-4", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  cardVariants,
}
